import { useEffect, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ConfettiIcon } from '@phosphor-icons/react';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';
import Step from 'src/components/ui/Step';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { useLocation } from 'react-router-dom';
import RightPanelShell from 'src/components/layouts/RightPanelShell';

export default function WikiGuide() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();
  const location = useLocation();

  const [steps, setSteps] = useState<{
    tour: 'incomplete' | 'loading' | 'complete';
    filter: 'incomplete' | 'loading' | 'complete';
    search: 'incomplete' | 'loading' | 'complete';
    chat: 'incomplete' | 'loading' | 'complete';
  }>({
    tour: 'loading',
    filter: 'incomplete',
    search: 'incomplete',
    chat: 'incomplete'
  });

  // Navigate underlying page to /wiki once when opening Wiki Assistant
  useEffect(() => {
    if (!window.location.pathname.includes('/wiki')) {
      navigate('/wiki');
    }
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Immediately mark chat step complete when a wiki question is asked
  useEffect(() => {
    const handleAsked = () => {
      setSteps((prev) => (prev.chat === 'complete' ? prev : { ...prev, chat: 'complete' }));
    };
    try {
      document.addEventListener('innex-wiki-question-asked', handleAsked as EventListener);
    } catch {}
    return () => {
      try {
        document.removeEventListener('innex-wiki-question-asked', handleAsked as EventListener);
      } catch {}
    };
  }, []);

  // Auto-complete filter step when user applies a domain filter (e.g. HTM)
  useEffect(() => {
    const handleFilterApplied = () => {
      setSteps((prev) => {
        if (prev.filter === 'complete') return prev;
        const nextSearch = prev.search === 'incomplete' ? 'loading' : prev.search;
        return { ...prev, filter: 'complete', search: nextSearch };
      });
    };
    document.addEventListener('innex-wiki-filter-applied', handleFilterApplied as EventListener);
    return () => {
      document.removeEventListener('innex-wiki-filter-applied', handleFilterApplied as EventListener);
    };
  }, []);

  // Mark "Search and Open Document" as complete when navigating to a specific document page
  useEffect(() => {
    if (steps.search !== 'loading') return;
    const hasDocPath = window.location.pathname.includes('/wiki/') && !window.location.pathname.endsWith('/wiki');
    if (hasDocPath) {
      setSteps((prev) => {
        const nextChat = prev.chat === 'incomplete' ? 'loading' : prev.chat;
        return { ...prev, search: 'complete', chat: nextChat };
      });
    }
  }, [location.pathname, steps.search]);

  // If wiki tour already completed, reflect in first step
  useEffect(() => {
    const completed = localStorage.getItem('innex-wiki-tour-completed') === 'true';
    if (completed) {
      setSteps((s) => ({ ...s, tour: 'complete' }));
      setSteps((s) => ({ ...s, filter: 'loading' }));
    }
  }, []);

  // While tour is running, detect completion and advance states
  useEffect(() => {
    if (steps.tour !== 'loading') return;
    const id = setInterval(() => {
      const completed = localStorage.getItem('innex-wiki-tour-completed') === 'true';
      if (completed) {
        setSteps((s) => ({ ...s, tour: 'complete', filter: 'loading' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.tour]);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Wiki Assistant</h2>
    </div>
  );

  const allComplete =
    steps.tour === 'complete' &&
    steps.filter === 'complete' &&
    steps.search === 'complete' &&
    steps.chat === 'complete';

  const skipStep = () => {
    setSteps((s) => {
      const order = ['tour', 'filter', 'search', 'chat'] as const;
      const loadingIdx = order.findIndex((k) => s[k] === 'loading');
      const nextState = { ...s };
      if (loadingIdx >= 0) {
        nextState[order[loadingIdx]] = 'incomplete';
        const relNextIdx = order.slice(loadingIdx + 1).findIndex((k) => s[k] === 'incomplete');
        if (relNextIdx >= 0) {
          const idx = loadingIdx + 1 + relNextIdx;
          nextState[order[idx]] = 'loading';
        }
        return nextState;
      }
      const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
      if (firstIncompleteIdx >= 0) {
        nextState[order[firstIncompleteIdx]] = 'loading';
        return nextState;
      }
      return s;
    });
  };

  const footer = allComplete ? (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-wiki-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  ) : (
    <div className="flex flex-col gap-2">
      <Button variant="outline" size="md" className="w-full" onClick={skipStep}>
        Skip Step
      </Button>
      <Button variant="outline" size="md" className="w-full" onClick={() => navigate('/tutorials/wiki-page')}>
        Watch Wiki Tutorials
      </Button>
    </div>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-2">
        {/* Helper to set an active step: clicked becomes loading, any current loading becomes incomplete */}
        {(() => {
          const setActiveStep = (key: 'tour' | 'filter' | 'search' | 'chat') => {
            setSteps((prev) => {
              if (prev[key] === 'loading') return prev;
              const next = { ...prev } as typeof prev;
              if (next.tour === 'loading') next.tour = 'incomplete';
              if (next.filter === 'loading') next.filter = 'incomplete';
              if (next.search === 'loading') next.search = 'incomplete';
              if (next.chat === 'loading') next.chat = 'incomplete';
              next[key] = 'loading';
              return next;
            });
          };

          return (
            <>
              <Step
                title="Complete Tour"
                state={steps.tour}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-wiki-tour-completed');
                  } catch {}
                  setActiveStep('tour');
                }}
                children={
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      Start our short guided tour to familiarise yourself with the Wiki.
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        try {
                          window.dispatchEvent(new Event('start-wiki-tour'));
                        } catch {}
                      }}
                    >
                      Start tour
                    </Button>
                  </div>
                }
              />

              <Step
                title="Apply a filter"
                state={steps.filter}
                onTitleClick={() => setActiveStep('filter')}
                children={
                  steps.filter === 'loading' ? (
                    <div className="text-sm text-content-subtle">
                      1. Go to "Domains" on the left hand side.
                      <br /> 2. Apply the "HTM" filter by clicking the checkbox.
                    </div>
                  ) : undefined
                }
              />
              <Step
                title="Search and Open Document"
                state={steps.search}
                onTitleClick={() => setActiveStep('search')}
                children={
                  steps.search === 'loading' ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-content-subtle">
                        1. Using the search bar above the list of documents. <br /> 2. Search for "Electrical Services
                        Supply and Distribution". <br /> 3. Click the document to open it.
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setSteps((s) => ({
                            ...s,
                            search: 'complete',
                            chat: s.chat === 'incomplete' ? 'loading' : s.chat
                          }));
                        }}
                      >
                        I opened the document
                      </Button>
                    </div>
                  ) : undefined
                }
              />
              <Step
                title="Chat with Document"
                state={steps.chat}
                isLast={!allComplete}
                onTitleClick={() => setActiveStep('chat')}
                children={
                  steps.chat === 'loading' ? (
                    <div className="text-sm text-content-subtle">
                      1. Click the "Chat" button at the bottom of the document. <br /> 2. Ask a question about the
                      document, you can use the one below for example. <br />
                      <br />{' '}
                      <i>
                        "What is the normal supply frequency for electrical systems in healthcare premises, and what is
                        its tolerance?"
                      </i>
                      <br /> <br />
                      3. Ask any follow ups as needed.
                    </div>
                  ) : undefined
                }
              />
              {allComplete && (
                <Step
                  title="Congrats, you're all set!"
                  state="loading"
                  isLast={true}
                  icon={<ConfettiIcon size={28} className="text-secondary-blue" />}
                >
                  <div className="text-sm text-content-subtle">
                    You're one step closer to getting the most out of CompliMind and saving yourself hours at work!
                  </div>
                </Step>
              )}
            </>
          );
        })()}
      </div>
    </RightPanelShell>
  );
}
